import { useNavigate } from 'react-router-dom';

export interface ErrorContext {
  resource: 'issuer' | 'badge' | 'award';
  action: 'create' | 'update' | 'delete';
  /** Sanitized request payload (omit base64 image data — keeps prefill URL small). */
  payload?: Record<string, unknown>;
}

interface ActionErrorPanelProps {
  error: Error | null | undefined;
  context: ErrorContext;
  onRetry?: () => void;
  onDismiss?: () => void;
}

const RESOURCE_LABEL: Record<ErrorContext['resource'], string> = {
  issuer: 'issuer',
  badge: 'badge',
  award: 'credential',
};

/**
 * Map common server error signatures to a friendlier headline. Falls back to
 * a generic "Something went wrong" so the user always gets context.
 */
function classify(error: Error, ctx: ErrorContext): { headline: string; hint?: string } {
  const m = (error.message || '').toLowerCase();
  if (m.includes('insufficient credits') || m.includes('quota reached') || m.includes('quota exceeded')) {
    return {
      headline: ctx.resource === 'award'
        ? 'Monthly credential limit reached'
        : `${RESOURCE_LABEL[ctx.resource]} limit reached on your plan`,
      hint: ctx.resource === 'award'
        ? 'Credentials reset at the start of each month. Upgrade your plan for a higher monthly allowance.'
        : 'Delete an existing item or upgrade your plan to create more.',
    };
  }
  if (m.includes('limit reached')) {
    return { headline: `${RESOURCE_LABEL[ctx.resource]} plan limit reached`, hint: error.message };
  }
  if (m.includes('not authorized') || m.includes('unauthorized') || m.includes('forbidden')) {
    return { headline: 'Not authorized', hint: 'Try signing out and back in. If the problem persists, contact support.' };
  }
  if (m.includes('verified before') || m.includes('must be verified')) {
    return { headline: 'Issuer must be verified first', hint: 'Verify the issuer (open the issuer card) before creating badges or credentials under it.' };
  }
  if (m.includes('missing required') || m.includes('parametererror') || m.includes('invalid')) {
    return { headline: "Couldn't validate your input", hint: error.message };
  }
  if (m.includes('not found')) {
    return { headline: 'Not found', hint: error.message };
  }
  return { headline: `Couldn't ${ctx.action} the ${RESOURCE_LABEL[ctx.resource]}`, hint: 'Something went wrong on our end.' };
}

/** Sanitize a payload before encoding into the URL (strip base64 images). */
function sanitizePayload(p: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!p) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) {
    if (typeof v === 'string' && v.length > 200 && /^data:image|^[A-Za-z0-9+/=]{200,}$/.test(v)) {
      out[k] = `<${v.length} chars omitted>`;
    } else {
      out[k] = v;
    }
  }
  return out;
}

function buildPrefill(error: Error, ctx: ErrorContext): string {
  const subject = `Couldn't ${ctx.action} ${RESOURCE_LABEL[ctx.resource]}: ${error.message.slice(0, 80)}`;
  const body = [
    `Hi support team,`,
    ``,
    `I tried to ${ctx.action} a ${RESOURCE_LABEL[ctx.resource]} on the dashboard but got this error:`,
    ``,
    `> ${error.message}`,
    ``,
    `Context:`,
    `- Resource: ${ctx.resource}`,
    `- Action: ${ctx.action}`,
    `- Time (browser): ${new Date().toISOString()}`,
    `- Page: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
    `- User agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
    ``,
    `Request payload (sensitive fields omitted):`,
    '```json',
    JSON.stringify(sanitizePayload(ctx.payload), null, 2),
    '```',
    ``,
    `Thanks!`,
  ].join('\n');
  // Encode as base64 of a JSON envelope so SupportPage can reconstruct subject+body
  const envelope = { subject, body, category: 'bug' };
  return btoa(unescape(encodeURIComponent(JSON.stringify(envelope))));
}

export function ActionErrorPanel({ error, context, onRetry, onDismiss }: ActionErrorPanelProps) {
  const navigate = useNavigate();
  if (!error) return null;
  const { headline, hint } = classify(error, context);
  const prefill = buildPrefill(error, context);

  return (
    <div
      data-testid={`action-error-${context.resource}`}
      role="alert"
      className="my-3 rounded-lg border border-red-300 bg-red-50 p-4 text-sm"
    >
      <p className="font-semibold text-red-900">{headline}</p>
      {hint && <p className="mt-1 text-red-800">{hint}</p>}
      <p className="mt-2 font-mono text-[11px] text-red-700/80 break-words">{error.message}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="action-error-create-ticket"
          onClick={() => navigate(`/support?prefill=${encodeURIComponent(prefill)}`)}
          className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
        >
          Create a support ticket
        </button>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="ml-auto text-[11px] text-red-700/70 hover:text-red-900"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
